import { ReactElement } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'
import { isEmpty } from 'lodash'

import { FormattedDateTime } from '@10xLabs/react-web/components/FormattedDateTime'
import { StyledLink } from 'components/v2/Link'

import { Dropdown } from 'components/v2/Dropdown'
import { TableContainer, Table, Thead, Th, Tbody, Tr, Td } from 'components/v2/Table'
import { useLogin } from 'features/Login/hooks'
import { isSuperAdmin } from 'features/Users/utils'
import { GTCampaignType, GTCoupon, GTSubCampaign } from 'gql/types.generated'
import { ISubcampaignListProps as ISubcampaignsListProps } from './types'
import { campaignTypeMessages } from 'features/Campaigns/components/CampaignType/messages'
import { Link } from 'react-router-dom'

const DurationWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;

  > div {
    margin-left: 2px;
  }
`

const getSegmentsValue = (subcampaign: GTSubCampaign): ReactElement => {
  const legSegments = subcampaign.trip?.legSegments ?? []
  if (isEmpty(legSegments)) {
    return <FormattedMessage id="dictionary.all" defaultMessage="All segments" />
  }
  return (
    <Dropdown toggle={<StyledLink>{legSegments?.length}</StyledLink>} noPadding fitContent>
      <Table>
        <Thead>
          <Tr>
            <Th>
              <FormattedMessage id="dictionary.origin" defaultMessage="Origin" />
            </Th>
            <Th>
              <FormattedMessage id="dictionary.destination" defaultMessage="Destination" />
            </Th>
          </Tr>
        </Thead>
        <Tbody>
          {legSegments.map(({ origin, destination }, index) => {
            return (
              <Tr key={index}>
                <Td>{origin?.name}</Td>
                <Td>{destination?.name}</Td>
              </Tr>
            )
          })}
        </Tbody>
      </Table>
    </Dropdown>
  )
}
export const SubcampaignsList = ({ subcampaigns }: ISubcampaignsListProps): ReactElement => {
  const intl = useIntl()
  const login = useLogin()

  return (
    <TableContainer overflow>
      <Table marginBottom>
        <Thead>
          <Tr>
            <Th>
              <FormattedMessage id="dictionary.trip" defaultMessage="Trip" />
            </Th>
            <Th>
              <FormattedMessage id="dictionary.campaign" defaultMessage="Campaign" />
            </Th>
            <Th>
              <FormattedMessage id="subcampaingsList.couponsValidity" defaultMessage="Coupons validity" />
            </Th>
            <Th>
              <FormattedMessage id="dictionary.authorization" defaultMessage="Authorization" />
            </Th>
            <Th>
              <FormattedMessage id="dictionary.coupons" defaultMessage="Type" />
            </Th>
            <Th>
              <FormattedMessage id="dictionary.coupons" defaultMessage="Coupons" />
            </Th>
            <Th>
              <FormattedMessage id="dictionary.usage" defaultMessage="Usage" />
            </Th>
            <Th>
              <FormattedMessage id="dictionary.legSegments" defaultMessage="Leg Segments" />
            </Th>
            {isSuperAdmin(login.accessToken.conditions) && (
              <Th>
                <FormattedMessage id="dictionary.company" defaultMessage="Company" />
              </Th>
            )}
          </Tr>
        </Thead>
        <Tbody>
          {subcampaigns.map((subcampaign: TSFixMe): ReactElement => {
            const couponDuration = {
              startsAt: subcampaign.coupons[0]?.startsAt,
              endsAt: subcampaign.coupons[0]?.endsAt
            }
            return (
              <Tr key={subcampaign.id}>
                <Td>
                  <StyledLink
                    as={Link}
                    to={`/trips/${subcampaign.trip.id}/incidence-coupons?subcampaignId=${subcampaign.id}`}
                  >{`${subcampaign.trip.origin?.name} - ${subcampaign.trip.destination?.name}`}</StyledLink>
                </Td>
                <Td>{subcampaign.campaign.name}</Td>
                <Td>
                  <DurationWrapper>
                    {couponDuration.startsAt ? (
                      <>
                        <FormattedMessage id="subcampaigns.incidences.startsAt.label" defaultMessage="Starts:" />{' '}
                        <FormattedDateTime date={couponDuration.startsAt} intl={intl} />
                      </>
                    ) : null}
                  </DurationWrapper>
                  <DurationWrapper>
                    {couponDuration.endsAt ? (
                      <>
                        <FormattedMessage id="subcampaigns.incidences.endsAt.label" defaultMessage="Ends:" />
                        <FormattedDateTime date={couponDuration.endsAt} intl={intl} />
                      </>
                    ) : null}
                  </DurationWrapper>
                </Td>
                <Td>
                  <div>{subcampaign.authorizer}</div>
                </Td>
                <Td>
                  {campaignTypeMessages[subcampaign.campaign.type as GTCampaignType]
                    ? intl.formatMessage(campaignTypeMessages[subcampaign.campaign.type as GTCampaignType])
                    : subcampaign.campaign.type}
                </Td>
                <Td>{subcampaign.coupons.length}</Td>
                <Td>
                  <div>
                    <FormattedMessage
                      id="subcampaigns.incidences.usage.value"
                      defaultMessage="{usedCount}"
                      values={{
                        usedCount: subcampaign.coupons.reduce((acc: number, coupon: GTCoupon) => {
                          return acc + coupon.usedCount
                        }, 0)
                      }}
                    />
                  </div>
                </Td>
                <Td>
                  <div>{getSegmentsValue(subcampaign)}</div>
                </Td>
                {isSuperAdmin(login.accessToken.conditions) && <Td>{subcampaign.campaign.company.name}</Td>}
              </Tr>
            )
          })}
        </Tbody>
      </Table>
    </TableContainer>
  )
}
